import {
  Paper,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Typography,
  useTheme
} from '@mui/material';
import { format } from 'date-fns';
import { compact, sortBy } from 'lodash';
import { useMemo } from 'react';

import { ComponentWeightBuffer, ExtendedFoodComponentForPrint, FoodComponentMethodStep } from '../../../libs';
import { Ingredient } from '../../../libs/types';
import { ComponentService } from '../../../libs/utils/componentService';
import { roundToTwoDecimals } from '../../../libs/utils/helperFunctions';
import { calculateBufferPercentage, calculateFractionWeight } from '../helpers';
import IngredientRow, { StyledTableCell } from './IngredientRow';

interface Props {
  component: ExtendedFoodComponentForPrint;
  date: string;
  kitchen?: string;
  buffers?: ComponentWeightBuffer[];
}

const ComponentExportTable = ({ component, date, kitchen, buffers = [] }: Props) => {
  const theme = useTheme();

  const bufferPercentage = useMemo(() => {
    return calculateBufferPercentage(component.totalCookedWeight, buffers);
  }, [component, buffers]);

  const showBuffer = buffers.length > 0 && bufferPercentage > 0;

  const bufferedCookedWeight = useMemo(() => {
    const totalCookedWeight = component.totalCookedWeight;
    const bufferMultiplier = bufferPercentage > 0 ? 1 + bufferPercentage / 100 : 1;
    return totalCookedWeight * bufferMultiplier;
  }, [component, bufferPercentage]);

  const structuredIngredients = useMemo(() => {
    let currentHeader: string | null | undefined;
    const ingredients = component.ingredients || [];

    // Group ingredients: first those without headers, then sorted by header
    const withHeader = compact(ingredients.filter((ing) => ing.header));
    const sortedWithHeader = sortBy(withHeader, 'header');
    const withoutHeader = compact(ingredients.filter((ing) => !ing.header));
    const orderedIngredients = [...withoutHeader, ...sortedWithHeader];

    const updatedStructuredIngredients: Array<
      { type: 'header'; header: string } | { type: 'ingredient'; ingredient: Ingredient; index: number }
    > = [];

    for (const [index, ingredient] of orderedIngredients.entries()) {
      if (ingredient.header !== currentHeader && ingredient.header) {
        currentHeader = ingredient.header;
        updatedStructuredIngredients.push({ type: 'header', header: currentHeader });
      }
      updatedStructuredIngredients.push({ type: 'ingredient', ingredient, index });
    }

    return updatedStructuredIngredients;
  }, [component.ingredients]);

  const numberOfHeaders = useMemo(
    () => structuredIngredients.filter((item) => item.type === 'header').length,
    [structuredIngredients]
  );

  const getComponentName = () => {
    return component.name?.en ?? (typeof component.name === 'string' ? component.name : 'Unknown Component');
  };

  const totalCookedWeight = component.totalCookedWeight;
  const totalRawWeight = component.totalRawWeight;

  return (
    <TableContainer component={Paper} sx={{ marginX: '10px' }}>
      <Stack
        sx={{ display: 'flex', flexDirection: 'row', gap: '8px', marginLeft: '16px', marginTop: '16px', marginBottom: '2px' }}
      >
        <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>{getComponentName()} |</Typography>
        <Stack sx={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
          <Typography sx={{ fontSize: '18px' }}>Raw Weight: </Typography>
          <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>{Math.round(totalRawWeight)}</Typography>
        </Stack>
        <Typography sx={{ fontSize: '18px' }}>|</Typography>
        <Stack sx={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
          <Typography sx={{ fontSize: '18px' }}>Cooked Weight: </Typography>
          <Typography sx={{ fontSize: '18px', fontWeight: 'bold' }}>{Math.round(totalCookedWeight)}</Typography>
        </Stack>
      </Stack>

      {showBuffer && (
        <Stack
          sx={{ display: 'flex', flexDirection: 'row', gap: '8px', marginLeft: '16px', marginTop: '2px', marginBottom: '2px' }}
        >
          <Stack sx={{ display: 'flex', flexDirection: 'row', gap: '4px' }}>
            <Typography sx={{ fontSize: '18px', color: '#4CAF50' }}>Buffer-adjusted Cooked Weight: </Typography>
            <Typography sx={{ fontSize: '18px', fontWeight: 'bold', color: '#4CAF50' }}>
              {Math.round(totalCookedWeight * (1 + bufferPercentage / 100))}
            </Typography>
          </Stack>
        </Stack>
      )}

      <Stack
        sx={{ display: 'flex', flexDirection: 'row', gap: '8px', marginLeft: '16px', marginTop: '2px', marginBottom: '16px' }}
      >
        <Typography sx={{ fontSize: '18px' }}>{component.brand} |</Typography>
        <Typography sx={{ fontSize: '18px' }}>{component.country} |</Typography>
        <Typography sx={{ fontSize: '18px' }}>{kitchen} |</Typography>
        <Typography sx={{ fontSize: '18px' }}>For {format(new Date(date), 'dd/MM/yyyy')}</Typography>
        {showBuffer && (
          <Typography sx={{ fontSize: '18px', color: '#4CAF50', fontWeight: 500 }}>| Buffer: {bufferPercentage}%</Typography>
        )}
      </Stack>

      <Table aria-label="Excel Table">
        <TableHead>
          {showBuffer && (
            <TableRow>
              <TableCell colSpan={5} sx={{ textAlign: 'center', fontSize: '16px', fontWeight: 600, color: '#4CAF50', py: '8px' }}>
                Buffer-adjusted quantities
              </TableCell>
            </TableRow>
          )}
          <TableRow sx={{ bgcolor: theme.palette.neutral300 }}>
            <StyledTableCell sx={{ width: '18%' }}>Ingredient</StyledTableCell>
            <StyledTableCell sx={{ width: '16%', padding: 0 }}>Quantity Before Prep</StyledTableCell>
            <StyledTableCell sx={{ width: '16%', padding: 0 }}>Quantity After Prep</StyledTableCell>
            <StyledTableCell sx={{ width: '5%', padding: 0, textAlign: 'start' }}>Unit</StyledTableCell>
            <StyledTableCell sx={{ width: '45%', textAlign: 'center' }}>Method</StyledTableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {structuredIngredients.map((sIngredient, idx) => {
            if (sIngredient.type === 'header') {
              return (
                <TableRow key={sIngredient.header}>
                  <TableCell colSpan={4} sx={{ fontSize: '18px', fontWeight: 700, py: '10px' }}>
                    {sIngredient.header}
                  </TableCell>
                </TableRow>
              );
            } else {
              const { ingredient, index } = sIngredient;
              return (
                <IngredientRow
                  key={ingredient.id ?? idx}
                  ingredient={ingredient}
                  index={index}
                  bufferedCookedWeight={bufferedCookedWeight}
                  component={component}
                  componentMethod={component.method?.filter((m): m is FoodComponentMethodStep => !!m) ?? []}
                  totalIngredients={
                    (component.ingredients?.length ?? 0) + (component.childComponents?.length ?? 0) + numberOfHeaders
                  }
                />
              );
            }
          })}
          {component.childComponents?.map((childComponent, index) => {
            if (!childComponent) return null;

            const bufferMultiplier = bufferPercentage > 0 ? 1 + bufferPercentage / 100 : 1;
            const bufferedCookedWeight = component.totalCookedWeight * bufferMultiplier;

            const bufferedChildWeight = roundToTwoDecimals(
              calculateFractionWeight(
                component,
                ComponentService.calculateComponentWeight(
                  childComponent.cups,
                  childComponent.measurementUnit,
                  childComponent.weight ?? 0,
                  childComponent.quantity || 0
                ),
                bufferedCookedWeight
              )
            );

            return (
              <TableRow key={childComponent.id + '#' + index} sx={{ fontWeight: 'bold' }}>
                <StyledTableCell>{childComponent.name?.en ?? childComponent.id}</StyledTableCell>
                <StyledTableCell sx={{ paddingLeft: 0 }}></StyledTableCell>
                <StyledTableCell sx={{ paddingLeft: 0 }}>{bufferedChildWeight}</StyledTableCell>
                <StyledTableCell sx={{ paddingLeft: 0 }}>g</StyledTableCell>
              </TableRow>
            );
          })}
        </TableBody>
      </Table>
      <Stack sx={{ padding: '16px', display: 'flex', flexDirection: 'row', justifyContent: 'flex-end' }}>
        <Typography sx={{ fontSize: '10px', color: '#666' }}>
          Printed on {format(new Date(), 'dd/MM/yyyy HH:mm:ss')} from Components Check
        </Typography>
      </Stack>
    </TableContainer>
  );
};

export default ComponentExportTable;
